import { Loading } from "src/components/utils/Loading.tsx";
import { ScoreDetailContextProvider } from "src/context/ScoreDetail/provider.tsx";
import { URLHandler } from "src/services/URLHandler";
import { useLocation } from "react-router";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { useGetProductScores } from "src/api/client-api/olapScores/useGetProductScores";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { OlapParamsService } from "src/context/OlapScores/OlapParamsService";
import { useGetProductPost } from "src/api/client-api/post/useGetProductPost";
import { useMemo } from "react";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { getScoreDetailRemoteState } from "src/context/ScoreDetail/scoreDetailRemoteState";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { buildProductScoreDetailDimensions } from "./productScoreDetailDimensions";

type ProductOneScoreContextProviderProps = {
    children: ReactNode | ReactNode[];
    companyId: number;
    productId: number;
    scoreId: number;
    ssqVersion?: SsqVersion;
    isIntermediateProduct?: boolean;
};

export const ProductOneScoreDetailProvider = (props: ProductOneScoreContextProviderProps) => {
    const { children, companyId, productId, scoreId, ssqVersion, isIntermediateProduct = false } = props;
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const { scoresWithVariations, selectedVariations, getEntityScore } = useEntityScores();
    const isProductUxV2 = useIsProductUxV2();

    const location = useLocation();
    const score = getEntityScore(scoreId);

    // Extraire l'URL de retour depuis les paramètres pour la propager
    const urlHandlerRoutes = isIntermediateProduct ? URLHandler.intermediateProduct : URLHandler.product;
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const withReturnUrl = (url: string) => (returnUrl ? `${url}?returnUrl=${encodeURIComponent(returnUrl)}` : url);

    const scoreDetailDimensions = useMemo(() => {
        return buildProductScoreDetailDimensions(ssqVersion);
    }, [ssqVersion]);

    const initialDimensions = OlapParamsService.getInitialDimensions(scoreDetailDimensions);
    const scoresParams = useOlapScoresParams({
        scores: {
            ids: [scoreId],
            dimensions: initialDimensions,
            variations: selectedVariations.query,
            ...(isProductUxV2 ? { subscores: true } : {}),
        },
        prevScores: true,
    });
    const areAggregationsRequested = scoresParams.query.scores?.aggregations !== undefined;

    const {
        data: scores,
        error: scoreQueryError,
        isError: isScoreQueryError,
        isFetched,
        isLoading,
        isRefetching,
        refetch: retryScoreQuery,
    } = useGetProductScores({
        productId,
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        scoresQuery: scoresParams.query,
        ...(isProductUxV2 ? { enabled: score !== undefined } : {}),
    });

    const { refetch: fetchPostForAnswers } = useGetProductPost({
        productId,
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        enabled: false,
    });

    const isScoreQueryPending = isLoading || isRefetching || isFetched === false;

    const scoreQueryState = getScoreDetailRemoteState({
        score: scores?.olapScores[scoreId],
        isLoading: isScoreQueryPending,
        error: isScoreQueryError ? scoreQueryError : undefined,
    });

    if (score === undefined) {
        return isProductUxV2 ? <ScoreQueryError context="details" status="not-found" /> : <Loading />;
    }

    const hideSeeMoreDetails = (dimension: string) => {
        // `transport_lines` is a leaf dimension (no further nesting). Without this guard
        // the Distribution chart would still attach a click handler — because
        // `fetchPostForAnswers` is defined — and bounce to `showPropertyModal`, which
        // is a no-op for anything that is not a `properties` / `subproperties` value.
        // Result: pointer cursor + dead click. Same rationale as `ingredients` / `packagings`.
        return (
            dimension === "ingredients" ||
            dimension === "transformations" ||
            dimension === "packagings" ||
            dimension === "transport_lines" ||
            dimension === "distribution" ||
            dimension === "consumption"
        );
    };

    const scoreWithVariations = scoresWithVariations?.filter(
        (scoreWithVariation) => scoreWithVariation.scoreId === scoreId,
    );

    return (
        <ScoreDetailContextProvider
            score={score}
            olapScores={scores?.olapScores[scoreId]}
            olapPrevScores={scores?.olapPrevScores?.[scoreId]}
            scoresParams={scoresParams}
            scoreDetailDimensions={scoreDetailDimensions}
            ignoredDrillDownDimensions={["multivalues"]}
            areAggregationsRequested={areAggregationsRequested}
            olapLocalizedLabelContext={{ ssqVersion }}
            fetchPostForAnswers={ssqVersion ? fetchPostForAnswers : undefined}
            hideSeeMoreDetails={hideSeeMoreDetails}
            selectedVariations={selectedVariations}
            scoreWithVariations={scoreWithVariations}
            ssqVersion={ssqVersion}
            isLoading={isScoreQueryPending}
            scoreQueryState={scoreQueryState}
            scoreQueryError={isScoreQueryError ? scoreQueryError : undefined}
            retryScoreQuery={retryScoreQuery}
            prevCampaignLabel={scores?.prevCampaignName}
            goBackLink={
                returnUrl ?
                    `${urlHandlerRoutes.scores(companyId, productId)}?returnUrl=${encodeURIComponent(returnUrl)}`
                :   urlHandlerRoutes.scores(companyId, productId)
            }
            tabConfig={{
                distribution: {
                    link: (scoreId) => {
                        const distUrl = urlHandlerRoutes.scoreDistribution(companyId, productId, Number(scoreId));
                        return withReturnUrl(distUrl);
                    },
                },
                ...(isProductUxV2 ?
                    {
                        evolution: {
                            link: (scoreId: string | number) =>
                                withReturnUrl(
                                    `${urlHandlerRoutes.score(companyId, productId, Number(scoreId))}/evolution`,
                                ),
                        },
                        comparison: {
                            link: (scoreId: string | number) =>
                                withReturnUrl(
                                    `${urlHandlerRoutes.score(companyId, productId, Number(scoreId))}/comparison`,
                                ),
                        },
                    }
                :   {}),
            }}
        >
            {children}
        </ScoreDetailContextProvider>
    );
};
